import { useSearchParams } from 'react-router-dom'
import {
	PageHeader,
	Tabs,
	TabList,
	Tab,
	TabPanel
} from '@clickguard/design-system'
import { AdAccountsTab } from './ad-accounts-tab'
import { CredentialsTab } from './credentials-tab'
import { CrmTab } from './crm-tab'
import { useFeatureFlag } from 'services/feature-flags'

type ConnectionsTab = 'ad-accounts' | 'credentials' | 'crm'

/**
 * Settings › Connections (DEV-2352).
 *
 * Single page replacing the old sidebar "Ad Accounts" and "Authorizations"
 * pages. Ad accounts (default), Credentials, and the feature-gated CRM tab
 * use `?tab=` so redirects and deep links land on the right tab.
 */
export const ConnectionsPage = () => {
	const [searchParams, setSearchParams] = useSearchParams()
	const leadFormsEnabled = useFeatureFlag('form-protection-enabled')

	const activeTab: ConnectionsTab =
		searchParams.get('tab') === 'credentials'
			? 'credentials'
			: leadFormsEnabled && searchParams.get('tab') === 'crm'
			? 'crm'
			: 'ad-accounts'

	const handleTabChange = (value: string) => {
		const next = new URLSearchParams(searchParams)
		next.set('tab', value)
		setSearchParams(next, { replace: true })
	}

	return (
		<div className="flex flex-col gap-4 pr-2">
			<PageHeader
				layout="stacked"
				title="Connections"
				subtitle="Setup and manage your ad accounts protection settings"
			/>
			<div className="rounded-xl border border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface))] p-4">
				<Tabs value={activeTab} onChange={handleTabChange} size="sm">
					<TabList fullBleed>
						<Tab value="ad-accounts" label="Ad accounts" />
						<Tab value="credentials" label="Credentials" />
						{leadFormsEnabled && <Tab value="crm" label="CRM" />}
					</TabList>
					<TabPanel value="ad-accounts">
						<AdAccountsTab />
					</TabPanel>
					<TabPanel value="credentials">
						<CredentialsTab />
					</TabPanel>
					{leadFormsEnabled && (
						<TabPanel value="crm">
							<CrmTab />
						</TabPanel>
					)}
				</Tabs>
			</div>
		</div>
	)
}
